import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import useAlerts from '@/hooks/useAlerts';
import {
  downloadImagesAsZip,
  downloadSelectedImagesAsZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import { distributeTextCanvas } from '@/utils/coverImages/textDistribution';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';
import { createWatermarkTagImage } from '../shared';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
  isAiGenerated?: boolean;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset, isAiGenerated }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariation = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          async () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var10');

            const fullImage = new Image();
            fullImage.src = variation.imageSrc;
            fullImage.classList.add('full-image-var10');

            const imageWrap = document.createElement('div');
            imageWrap.classList.add('image-wrap-var10');
            imageWrap.appendChild(fullImage);
            imageHolder.appendChild(imageWrap);

            const watermark = createWatermarkTagImage(
              'actual-photo-var10',
              variation.watermarkTag,
              variation.watermarkPosition,
            );
            if (watermark) imageWrap.appendChild(watermark);

            const bottomText = document.createElement('div');
            const textBreak = document.createElement('span');
            bottomText.append(textBreak);
            const bottomTextRight = document.createElement('div');

            if (variation.circleImageSrc) {
              const circleImage = new Image();
              circleImage.src = variation.circleImageSrc;
              circleImage.classList.add('circle-image-var10');
              imageHolder.appendChild(circleImage);
            }

            // Wait for font to load before measuring text
            await document.fonts.ready;

            const bottomTextP = document.createElement('p');
            bottomText.classList.add('bottom-text-var10');
            if (variation.text) {
              let text = variation.text;
              if (variation.textBrakes) {
                bottomTextP.innerText = text;
              } else {
                text = distributeTextCanvas(text, {
                  maxWidth: 900,
                  fontFamily: 'SourceSansProBlack',
                  minFontSize:
                    variation.textFontSize !== 0 ? variation.textFontSize : 55,
                });
                bottomTextP.innerHTML = text;
              }
            }
            bottomTextRight.appendChild(bottomTextP);
            const bottomWatermark = document.createElement('div');
            bottomWatermark.classList.add('bottom-watermark-var10');
            bottomWatermark.innerText = 'boredpanda.com';

            bottomTextRight.append(bottomWatermark);
            bottomText.append(bottomTextRight);

            imageHolder.appendChild(bottomText);

            if (variation.textFontSize) {
              bottomText.style.fontSize = `${variation.textFontSize}px`;
            }

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder-var10',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      try {
        const validImages = await generateImagesInParallel(
          variations,
          generateImageVariation,
          (current) => {
            setCounter(current);
          },
        );

        setImages(validImages);
      } finally {
        setShowProgress(false);
        setCounter(0);
      }
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images?.length) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      } else {
        await downloadImagesAsZip(images, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                  isAiGenerated={isAiGenerated}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
